import { TooltipProvider } from '@radix-ui/react-tooltip';
import { PanelRightClose, PanelRightOpen } from 'lucide-react';

import type { GapDraftView } from '@momentum/types';

import { PaneResizer } from './PaneResizer';
import { EditsTab } from './inspector/EditsTab';
import { EvidenceTab } from './inspector/EvidenceTab';
import { NotesTab } from './inspector/NotesTab';
import { RawTab } from './inspector/RawTab';
import { RunTab } from './inspector/RunTab';
import { ValidatorTab } from './inspector/ValidatorTab';

/** Raw is last: it is what a reviewer reaches for when the five readings of the
 *  record in front of it did not answer the question. */
export const INSPECTOR_TABS = [
  'run',
  'validator',
  'evidence',
  'notes',
  'edits',
  'raw',
] as const;

export type InspectorTab = (typeof INSPECTOR_TABS)[number];

const TAB_LABEL: Record<InspectorTab, string> = {
  run: 'Run',
  validator: 'Validator',
  evidence: 'Evidence',
  notes: 'Notes',
  edits: 'Edits',
  raw: 'Raw',
};

interface InspectorRailProps {
  draft: GapDraftView;
  tab: InspectorTab;
  onTabChange: (tab: InspectorTab) => void;
  commentDraft: string;
  onCommentDraftChange: (text: string) => void;
  onPostComment: () => void;
  onDiscardComment: () => void;
  isPostingComment: boolean;
  composeFocusToken: number;
  /** The anchored comments whose passage is on screen in the reader right now. */
  matchedQuotes: ReadonlySet<string>;
  /** Scrolls a quoted passage into view and flashes it. */
  onRevealQuote: (quote: string) => void;
  /** The passage the unposted comment is anchored to, if any. */
  commentQuote: string | undefined;
  onClearCommentQuote: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onResize: (width: number) => void;
  onResetWidth: () => void;
}

/**
 * The pipeline zone: everything the run itself recorded, never mixed into the
 * reader.
 *
 * The rail is permanent rather than a drawer, which is the whole point of mockup
 * D — a reviewer checking whether a claim survived validation should not have to
 * open and close a panel twenty times, and the reader keeps its own column so the
 * product text is never overlapped.
 *
 * Nothing here falls back to a product field. Where the reader says "from run",
 * this rail is the run, and a value missing here means the pipeline did not
 * record it.
 *
 * Edits is the one tab that is about the product layer rather than the run, and
 * it belongs here anyway: it is provenance, which is what every other tab is
 * for. The reader shows what a field says now; this says who made it say that.
 *
 * It collapses to a strip rather than to nothing: a reviewer who has stopped
 * auditing the pipeline and just wants to read wants the width back, but the way
 * out has to stay visible or the four tabs are simply gone. Collapsed state lives
 * in the reader route, so it holds while walking the queue with J/K.
 *
 * Its width is draggable for the same reason: a validator's evidence notes are
 * paragraphs, and how much of one belongs on screen at once is a judgment about
 * the gap being read, not something a fixed column can settle.
 */
export function InspectorRail({
  draft,
  tab,
  onTabChange,
  commentDraft,
  onCommentDraftChange,
  onPostComment,
  onDiscardComment,
  isPostingComment,
  composeFocusToken,
  matchedQuotes,
  onRevealQuote,
  commentQuote,
  onClearCommentQuote,
  isCollapsed,
  onToggleCollapse,
  onResize,
  onResetWidth,
}: InspectorRailProps) {
  // Edits counts FIELDS rather than saves — the number of saves is not known
  // until the tab is opened and the history is fetched, and how much of the
  // draft a human has touched is the more useful of the two anyway.
  const counts: Partial<Record<InspectorTab, number>> = {
    evidence: draft.source.evidence.length,
    notes: draft.review.comments.length,
    edits: draft.editedPaths.length,
  };

  if (isCollapsed) {
    return (
      <aside className="gr-insp gr-insp-strip">
        <button
          className="gr-insp-toggle"
          type="button"
          onClick={onToggleCollapse}
          aria-expanded={false}
          title="Show the run inspector"
        >
          <PanelRightOpen />
        </button>
        {/* Turned on its side so the collapsed strip still says what it is —
            and carries the two counts, which are the reason to reopen it. */}
        <span className="gr-insp-spine">
          Inspector · {counts.evidence} evidence · {counts.notes} notes
        </span>
      </aside>
    );
  }

  return (
    <aside className="gr-insp">
      {/* Collapsed, the rail is a fixed 40px strip, so there is no width to
          drag — the toggle is the only control it has. */}
      <PaneResizer
        edge="start"
        label="Resize the inspector"
        onResize={onResize}
        onReset={onResetWidth}
      />

      <div className="gr-tabs" role="tablist">
        {INSPECTOR_TABS.map((inspectorTab) => (
          <button
            className="gr-tab"
            type="button"
            role="tab"
            aria-selected={tab === inspectorTab}
            key={inspectorTab}
            onClick={() => onTabChange(inspectorTab)}
          >
            {TAB_LABEL[inspectorTab]}
            {counts[inspectorTab] !== undefined && (
              <em>{counts[inspectorTab]}</em>
            )}
          </button>
        ))}

        <button
          className="gr-insp-toggle gr-insp-toggle-inline"
          type="button"
          onClick={onToggleCollapse}
          aria-expanded={true}
          title="Hide the run inspector"
        >
          <PanelRightClose />
        </button>
      </div>

      {/* One provider for the whole rail rather than one per icon: it is what
          carries the shared skip-delay, so moving between two `FieldInfo`s
          shows the second immediately instead of waiting out the delay again. */}
      <TooltipProvider delayDuration={220} skipDelayDuration={500}>
        <div className="gr-insp-body">
          {/* The read-only tabs are keyed on the draft so an open disclosure does
            not carry over to the next gap. Notes deliberately is not: remounting
            it would re-run its focus effect and steal the keyboard mid-queue. */}
          {tab === 'run' && <RunTab draft={draft} key={draft._id} />}
          {tab === 'validator' && (
            <ValidatorTab draft={draft} key={draft._id} />
          )}
          {tab === 'evidence' && <EvidenceTab draft={draft} key={draft._id} />}
          {tab === 'edits' && <EditsTab draft={draft} key={draft._id} />}
          {tab === 'raw' && <RawTab draft={draft} key={draft._id} />}
          {tab === 'notes' && (
            <NotesTab
              draft={draft}
              commentDraft={commentDraft}
              onCommentDraftChange={onCommentDraftChange}
              onPost={onPostComment}
              onDiscard={onDiscardComment}
              isPosting={isPostingComment}
              focusToken={composeFocusToken}
              matchedQuotes={matchedQuotes}
              onRevealQuote={onRevealQuote}
              commentQuote={commentQuote}
              onClearQuote={onClearCommentQuote}
            />
          )}
        </div>
      </TooltipProvider>
    </aside>
  );
}
